import * as React from "react";

import { Analytics, ResultCardSubHeading } from "@skv/core";
import { useViewModel } from "@skv/state";
import { pluraliser } from "@skv/util";

import {
  ResultCard,
  ResultCardFooter,
  ResultCardHeader,
  ResultCardLinkHeading,
  ResultCardLinkHeadingProps,
  ResultMetaList,
  ResultMetaListItem,
} from "~/ui/ResultCard";

import * as dayjs from "dayjs";
import { Checkbox } from "~/ui";

interface ApplicationsResultCardProps {
  id: number;
  to: ResultCardLinkHeadingProps["to"];
  title: string;
  organisation: string;
  organisationLogo?: string;
  appliedDate: string;
  location: string;
  source: string;
  hasVolunteered: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/* TODO : radio inpu change form submissions */
const ApplicationsResultCard: React.FC<ApplicationsResultCardProps> = ({
  id,
  to,
  title,
  location,
  organisation,
  organisationLogo,
  appliedDate,
  source,
  hasVolunteered,
  onChange,
}) => {
  const chkYesId = `${id}__volunteered__yes`;
  const chkNoId = `${id}__volunteered__no`;

  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <div className="">
          {!!organisationLogo && (
            <img
              css={`
                float: right;
              `}
              className="ml-3 mb-3"
              alt="Homeless Connect Perth logo"
              aria-hidden="true"
              // todo : figure out srcsets below
              src={organisationLogo}
              // src="https://d2ifwjn5voryku.cloudfront.net/Images/Organisation/90x60/logo-8104.jpg"
              // srcSet="https://d2ifwjn5voryku.cloudfront.net/Images/Organisation/180x120/logo-8104.jpg 2x,https://d2ifwjn5voryku.cloudfront.net/Images/Organisation/270x180/logo-8104.jpg 3x"
            />
          )}

          <ResultCardLinkHeading
            className="h1"
            stretchedLink={false}
            to={to}
            title={title}
          />

          <ResultCardSubHeading className="h3 text-dark">
            {organisation}
          </ResultCardSubHeading>
        </div>
      </ResultCardHeader>
      <ResultMetaList $listItemSpacing="3rem">
        {!!location && (
          <ResultMetaListItem icon="marker">{location}</ResultMetaListItem>
        )}

        <ResultMetaListItem>
          Applied: {appliedDate}, {source}
        </ResultMetaListItem>
      </ResultMetaList>

      <ResultCardFooter>
        <form className="d-flex flex-column flex-lg-row align-items-center gap-4 px-3 pt-2">
          <legend className="nsw-form__legend w-auto mb-0">
            Did you volunteer?
          </legend>
          <fieldset className="nsw-form__fieldset">
            <div
              className="d-flex flex-column flex-lg-row align-items-start gap-4"
              css={`
                label {
                  margin-top: 0;
                }
              `}
            >
              <Checkbox
                id={chkNoId}
                name="didVolunteer"
                type="radio"
                label="No, I didn't"
                checked={hasVolunteered === false}
                value={0}
                onChange={onChange}
              />
              <Checkbox
                id={chkYesId}
                name="didVolunteer"
                type="radio"
                label="Yes, I did"
                checked={hasVolunteered === true}
                value={1}
                onChange={onChange}
              />
            </div>
          </fieldset>
        </form>
      </ResultCardFooter>
    </ResultCard>
  );
};

interface ActivityApplicationsViewModel {
  applicationCount: number;
  applications: Application[];
}

interface Application {
  applicationId: number;
  applicationDate: string;
  hasVolunteered: boolean;
  location: string;
  opportunityId: number;
  opportunityName: string;
  opportunityUrl: string;
  organisationLogoUrl: string | null;
  organisationName: string;
  source: string;
}

export const Applications: React.FC = () => {
  const [{ data, error, status }, { updateViewModel }] =
    useViewModel<ActivityApplicationsViewModel>();

  const createChangeHandler =
    (applicationId: number, idx: number) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;

      const checked = !!parseInt(value, 10);

      const nextViewModel = updateViewModel((prev) => ({
        ...prev,
        applications: Object.assign([], prev.applications, {
          [idx]: {
            ...prev.applications[idx],
            hasVolunteered: checked,
          },
        }),
      }));

      // TODO : Handle success, error
      fetch("/my-activity/application", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          applicationId: nextViewModel.applications[idx].applicationId,
          hasVolunteered: nextViewModel.applications[idx].hasVolunteered,
        }),
      }).then((res) => {
        if (res.ok) {
          return res.json();
        }

        throw Error("Unhandled network error");
      });
    };

  /* TODO : status */
  if (status === "loading") {
    return <p className="mt-3">Loading, please wait...</p>;
  }

  /* TODO : Error */
  if (status === "error") {
    return <p>Error</p>;
  }

  return (
    <>
      {/* TODO: create custom class? .myactivity-heading */}
      <h2
        className="display-3 font-weight-bold border-bottom mt-n2 mb-0"
        css={`
          padding-bottom: 1.25rem;
        `}
      >
        {pluraliser(data.applicationCount, {
          none: "You have no applications",
          plural: `You have added ${data.applicationCount} applications`,
          singular: "You have added 1 application",
        })}
      </h2>

      <ul className="list-unstyled">
        {data.applications.map((n, idx) => (
          <li key={n.applicationId} className="mt-0">
            <ApplicationsResultCard
              id={n.applicationId}
              appliedDate={dayjs(n.applicationDate).format("DD MMM YYYY")}
              location={n.location}
              organisation={n.organisationName}
              source={n.source}
              title={n.opportunityName}
              to={(loc) => ({
                pathname: n.opportunityUrl,
                state: {
                  ...loc.state,
                  referrer: {
                    ...loc,
                    state: { ...loc.state, children: "My Applications" },
                  },
                },
              })}
              hasVolunteered={n.hasVolunteered}
              onChange={createChangeHandler(n.applicationId, idx)}
            />
          </li>
        ))}
      </ul>

      <footer className="mt-5">
        {/* TODO: Apply correct style for h3 */}
        <h3 className="display-4 font-weight-bold">
          Can't find an application?
        </h3>
        <p>
          If you subscribed using a different email address, please sign in
          using that email address to see your applications.
        </p>
      </footer>

      <Analytics
        data={{
          subSection: "volunteers",
          subSubSection: "activity",
          subSubSubSection: "applications",
          pageTitle: "applications",
          loginStatus: "loggedin",
        }}
      />
    </>
  );
};
